import { CommonModule } from '@angular/common';
import { Component, inject, Input, OnInit } from '@angular/core';
import {
  processReportDetails,
  ProcessedReportDetails,
} from '../../../functions/process-report-details';
import { CompletedReport } from '../../../models/api/reports/all-reports-response';
import { BaseComponent } from '../../base/base.component';
import { HelpIconComponent } from '../../core/labels/help-icon.component';
import { IncludeLabelComponent } from '../../core/labels/include-label.component';
import { DetailItemComponent } from '../../core/panels/detail-panel/detail-item.component';
import { DetailPanelComponent } from '../../core/panels/detail-panel/detail-panel.component';
import { BytesPipe } from '../../../pipes/bytes.pipe';
import { CasePipe } from '../../../pipes/case.pipe';
import { LoadingPanelComponent } from '../../core/panels/loading-panel.component';
import { ReportsService } from '../../../services/reports/reports.service';
import { HandleLoadingState } from '../../../decorators/handle-loading-state.decorator';

@Component({
  selector: 'app-completed-request-view',
  imports: [
    DetailPanelComponent,
    DetailItemComponent,
    CommonModule,
    HelpIconComponent,
    IncludeLabelComponent,
    CasePipe,
    BytesPipe,
    LoadingPanelComponent,
  ],
  template: `
    <app-loading-panel
      [state]="loading ? 'Loading' : 'Loaded'"
      loadingStyle="Box"
      [loadingHeight]="192"
      [error]="error"
    >
      <ng-template #loaded>
        <div class="tw-flex tw-flex-col sm:tw-flex-row tw-gap-8 tw-w-full">
          <div class="sm:tw-basis-[40%]">
            <app-detail-panel [dataSource]="completedReportsData">
              <app-detail-item label="Name" name="reportName" />
              <app-detail-item label="Base report" name="outputName">
                <ng-template #value>
                  <div class="tw-flex tw-items-center tw-gap-2">
                    <span>{{ completedReportsData.outputName }}</span>
                    <app-help-icon
                      [helpText]="reportDescription"
                      [ariaLabel]="
                        (completedReportsData.outputName ?? 'Base report') +
                        ' information'
                      "
                    />
                  </div>
                </ng-template>
              </app-detail-item>
              <app-detail-item label="Client">
                <ng-template #value>
                  @if (
                    completedReportsData.contract &&
                    completedReportsData.clientName
                  ) {
                    {{ completedReportsData.contract }} -
                    {{ completedReportsData.clientName | case: 'titlecase' }}
                  }
                </ng-template>
              </app-detail-item>
              <app-detail-item
                label="Completed"
                name="dateCompleted"
                format="dt"
              />
              <app-detail-item
                label="Requested"
                name="dateScheduled"
                format="dt"
              />
              @if (asOfDate) {
                <app-detail-item label="As of Date">
                  <ng-template #value>{{ asOfDate }}</ng-template>
                </app-detail-item>
              }
              <!-- Commented out: Display all otherCriteria if requirements change
              @for (item of processed.otherCriteria; track $index) {
              <app-detail-item [label]="item.label">
                <ng-template #value>{{ item.value }}</ng-template>
              </app-detail-item>
              }
              -->
            </app-detail-panel>
          </div>

          <div class="sm:tw-basis-[30%]">
            <app-detail-panel [dataSource]="completedReportsData">
              <app-detail-item
                [label]="pagesOrRowsLabel"
                name="numberOfPages"
              />
              <app-detail-item label="Size">
                <ng-template #value>
                  {{ completedReportsData.fileSize | bytes }}
                </ng-template>
              </app-detail-item>
              <app-detail-item label="Format" name="fileFormat" />
              @if (completedReportsData.fileFormat !== 'PDF') {
                <app-detail-item label="Auto-fit column widths" format="b">
                  <ng-template #value>
                    {{ processed.autoFitColumnWidths }}
                  </ng-template>
                </app-detail-item>
              }
              <!-- @if (processed.pdfLogoSelection) {
              <app-detail-item label="PDF logo selection">
                <ng-template #value>
                  {{ processed.pdfLogoSelection }}
                </ng-template>
              </app-detail-item>
              } -->
            </app-detail-panel>
          </div>

          <div class="sm:tw-basis-[30%]">
            <app-detail-panel [dataSource]="processed.includeItems">
              <app-detail-item label="Include">
                <ng-template #value>
                  <app-include-label
                    [include]="processed.includeItems"
                    [exclude]="processed.excludeItems"
                  />
                </ng-template>
              </app-detail-item>
            </app-detail-panel>
          </div>
        </div>
      </ng-template>
    </app-loading-panel>
  `,
  styles: ``,
})
export class CompletedRequestViewComponent
  extends BaseComponent
  implements OnInit
{
  private reportsService = inject(ReportsService);

  @Input()
  completedReportsData: CompletedReport;

  processed: ProcessedReportDetails;

  reportDescription: string;

  loading = false;
  error: any = null;

  get pagesOrRowsLabel(): string {
    const format = this.completedReportsData?.fileFormat?.toUpperCase();
    return format === 'PDF' ? 'Pages' : 'Rows';
  }

  get asOfDate(): string | undefined {
    return this.processed?.otherCriteria?.find(
      (item) => item.label === 'As of Date',
    )?.value;
  }

  @HandleLoadingState()
  async ngOnInit() {
    const [reportDetails, reportDescription] = await Promise.all([
      this.api.reports.completedReportDetails(
        this.completedReportsData.completedRequestId,
      ),
      this.reportsService.getReportDescription(
        this.completedReportsData.reportMasterId,
      ),
    ]);
    this.processed = processReportDetails(reportDetails);
    this.reportDescription = reportDescription;
  }
}
